import { useState } from 'react'
import { Avatar } from '@/components/ui/shadcn/avatar'
import { type PostContent as LinkedInPostContent } from '@backend/lib/platforms/linkedin'
import { multiImageEntryUrl } from '@backend/lib/platforms/linkedin/multiImage'
import { PdfPreview } from '@/components/ui/PdfPreview'
import { AiLabeledImage } from './ai-label-preview-overlay'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

type Props = {
  account: { id: string; displayName: string; imageUrl?: string; subline?: string } | undefined
  postContent: LinkedInPostContent
  _onStartAuth: () => void
  postAsCarousel?: boolean
  carouselImageUrls?: string[]
  /** Draw the visible AI label that will be baked into image media at publish.
   *  LinkedIn has no native disclosure field, so on an image post this label is
   *  the only thing that discloses on the image itself. */
  showAiLabel?: boolean
  /** Appearance of that label, so the preview matches the user's settings. */
  aiLabelStyle?: AiLabelStyle
}

export function WorkspacePreviewLinkedin({
  account,
  postContent,
  _onStartAuth,
  postAsCarousel = false,
  carouselImageUrls,
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const multiImageUrls = postContent?.type === 'multiImage' ? postContent.urls : null
  const documentUrl = postContent?.type === 'document' ? postContent.url : null

  const contentKey = `${JSON.stringify(carouselImageUrls)}-${JSON.stringify(multiImageUrls)}-${documentUrl}`
  const [prevContentKey, setPrevContentKey] = useState(contentKey)
  if (prevContentKey !== contentKey) {
    setPrevContentKey(contentKey)
    setCurrentSlide(0)
  }

  const caption = postContent?.caption || ''
  const showShowMore = caption.length > 200

  const getDisplayCaption = () => {
    const caption = postContent?.caption || ''
    if (!caption) return 'Write a caption for your post'

    if (isExpanded || !showShowMore) {
      return caption
    }

    return caption.substring(0, 200) + '...'
  }

  return (
    <div className="mx-auto flex h-full w-full flex-col gap-3 rounded-lg p-3 pb-0 text-black">
      <div className="flex items-center gap-2">
        <ProfileHeader
          imageUrl={account?.imageUrl}
          displayName={account?.displayName || 'Your Name'}
          subline={undefined}
        />
      </div>

      {/* Post text */}
      <div>
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-black">
          {getDisplayCaption()}
        </p>
        {showShowMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800">
            {isExpanded ? 'Show less' : 'Show more...'}
          </button>
        )}
      </div>

      {/* Preview image / video */}
      <div className="-mx-3">
        {(() => {
          switch (postContent?.type) {
            case 'text':
            case 'reshare':
            case 'article':
            case 'poll':
              return null

            case 'image':
              return (
                <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                  <AiLabeledImage
                    src={postContent?.url || ''}
                    crossOrigin="anonymous"
                    className="max-h-full max-w-full object-contain"
                    showAiLabel={showAiLabel}
                    aiLabelStyle={aiLabelStyle}
                  />
                </div>
              )

            case 'multiImage': {
              const images = (postContent?.urls || []).map(multiImageEntryUrl)
              const imageCount = images.length

              if (imageCount === 0) return null

              // If postAsCarousel is true, show carousel UI
              if (postAsCarousel && imageCount > 1) {
                return (
                  <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                    {/* Slides container */}
                    <div
                      className="flex h-full transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                      {images.map((url) => (
                        <div
                          key={url}
                          className="flex h-full w-full shrink-0 items-center justify-center">
                          <AiLabeledImage
                            src={url}
                            crossOrigin="anonymous"
                            className="max-h-full max-w-full object-contain"
                            showAiLabel={showAiLabel}
                            aiLabelStyle={aiLabelStyle}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Navigation dots */}
                    {imageCount > 1 && (
                      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 transform gap-1">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-1.5 rounded-full transition-all ${
                              index === currentSlide ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Navigation arrows */}
                    {imageCount > 1 && (
                      <>
                        {currentSlide > 0 && (
                          <button
                            onClick={() => setCurrentSlide(currentSlide - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                            aria-label="Previous slide">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>
                        )}
                        {currentSlide < imageCount - 1 && (
                          <button
                            onClick={() => setCurrentSlide(currentSlide + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                            aria-label="Next slide">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )
              }

              // Default multiImage UI (not carousel)
              if (imageCount === 1) {
                return (
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                    <AiLabeledImage
                      src={images[0]}
                      crossOrigin="anonymous"
                      className="max-h-full max-w-full object-contain"
                      showAiLabel={showAiLabel}
                      aiLabelStyle={aiLabelStyle}
                    />
                  </div>
                )
              }

              if (imageCount === 2) {
                return (
                  <div className="flex gap-1">
                    {images.map((url) => (
                      <div
                        key={url}
                        className="flex aspect-square flex-1 items-center justify-center bg-gray-100">
                        <AiLabeledImage
                          src={url}
                          crossOrigin="anonymous"
                          className="max-h-full max-w-full object-contain"
                          showAiLabel={showAiLabel}
                          aiLabelStyle={aiLabelStyle}
                        />
                      </div>
                    ))}
                  </div>
                )
              }

              // 3+ images: first image large, then grid of remaining
              const firstImage = images[0]
              const remainingImages = images.slice(1)
              const maxVisibleRemaining = 2
              const visibleRemaining = remainingImages.slice(0, maxVisibleRemaining)
              const hiddenCount = remainingImages.length - maxVisibleRemaining

              return (
                <div className="flex flex-col gap-1">
                  {/* First image - large */}
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                    <AiLabeledImage
                      src={firstImage}
                      crossOrigin="anonymous"
                      className="max-h-full max-w-full object-contain"
                      showAiLabel={showAiLabel}
                      aiLabelStyle={aiLabelStyle}
                    />
                  </div>

                  {/* Remaining images in grid */}
                  {visibleRemaining.length > 0 && (
                    <div className="flex gap-1">
                      {visibleRemaining.map((url, index) => (
                        <div
                          key={url}
                          className="relative flex aspect-square flex-1 items-center justify-center bg-gray-100">
                          <AiLabeledImage
                            src={url}
                            crossOrigin="anonymous"
                            className="max-h-full max-w-full object-contain"
                            showAiLabel={showAiLabel}
                            aiLabelStyle={aiLabelStyle}
                          />
                          {/* Show +X overlay on last visible image if there are hidden images */}
                          {index === visibleRemaining.length - 1 && hiddenCount > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                              <span className="text-2xl font-semibold text-white">
                                +{hiddenCount}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            case 'video':
              return (
                <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                  <video
                    src={postContent?.url || ''}
                    className="max-h-full max-w-full object-contain"
                    controls
                    autoPlay
                    muted
                    playsInline
                  />
                </div>
              )

            case 'document':
              // If this is a carousel (multiple images converted to PDF), show carousel UI
              if (postAsCarousel && carouselImageUrls && carouselImageUrls.length > 0) {
                const images = carouselImageUrls
                const imageCount = images.length

                if (imageCount === 1) {
                  return (
                    <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                      <AiLabeledImage
                        src={images[0]}
                        crossOrigin="anonymous"
                        className="max-h-full max-w-full object-contain"
                        showAiLabel={showAiLabel}
                        aiLabelStyle={aiLabelStyle}
                      />
                    </div>
                  )
                }

                // Carousel with multiple images - swipeable
                return (
                  <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                    {/* Slides container */}
                    <div
                      className="flex h-full transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                      {images.map((url) => (
                        <div
                          key={url}
                          className="flex h-full w-full shrink-0 items-center justify-center">
                          <AiLabeledImage
                            src={url}
                            crossOrigin="anonymous"
                            className="max-h-full max-w-full object-contain"
                            showAiLabel={showAiLabel}
                            aiLabelStyle={aiLabelStyle}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Navigation dots */}
                    {imageCount > 1 && (
                      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 transform gap-1">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-1.5 rounded-full transition-all ${
                              index === currentSlide ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Navigation arrows */}
                    {imageCount > 1 && (
                      <>
                        {currentSlide > 0 && (
                          <button
                            onClick={() => setCurrentSlide(currentSlide - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                            aria-label="Previous slide">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>
                        )}
                        {currentSlide < imageCount - 1 && (
                          <button
                            onClick={() => setCurrentSlide(currentSlide + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                            aria-label="Next slide">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )
              }

              // Regular document (not a carousel) - show PDF preview
              return <PdfPreview src={postContent?.url || ''} />
          }
        })()}
      </div>

      {/* Engagement section - positioned after image/video content */}
      <div className="space-y-2">
        {/* Like and comment counts */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="font-semibold">42 reactions</span>
          <span>3 comments</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center border-t border-gray-200 pt-2">
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
            </svg>
            Like
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
            Comment
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
            </svg>
            Share
          </button>
        </div>
      </div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
  subline,
}: {
  imageUrl: string | undefined
  displayName: string
  subline: string | undefined
}) => {
  return (
    <div className="flex items-center gap-3">
      <ProfilePicture imageUrl={imageUrl} />
      <div className="text-left">
        <p className="text-sm font-semibold text-black">{displayName}</p>
        {subline && <p className="text-xs text-gray-600">{subline}</p>}
        <p className="text-xs text-gray-500">2h • 🌐</p>
      </div>
    </div>
  )
}

const ProfilePicture = ({ imageUrl }: { imageUrl: string | undefined }) => {
  return (
    <Avatar>
      {imageUrl ? (
        <div className="bg-background border-border flex h-full w-full items-center justify-center rounded-full border">
          <img src={imageUrl} />
        </div>
      ) : (
        <div className="w-full bg-gray-400"></div>
      )}
    </Avatar>
  )
}
